/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { GrammerErrorType } from "../User/type";
import HttpService from "../User/Api/interceptors";

interface PropsType {
    onValidationError?: (e: GrammerErrorType[]) => void;
    onLoading?: (e: boolean) => void;
    onChange: (v: string) => void;
    placeholder?: string;
    className?: string;
    value: string;
    onPaste?: React.ClipboardEventHandler;
    style?: React.CSSProperties;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;
}

const validateGrammar = async (text: string) => {
    const response = await HttpService.post("/api/users/check-grammer", {
        text: text,
        language: "en-US",
    });
    return response.data;
};

export default function GrammarValidator(props: PropsType) {
    const { onChange, value, className, style, onFocus, onBlur } = props;
    const [errors, setErrors] = useState<GrammerErrorType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const [html, setHtml] = useState(value || props?.placeholder || "Write Here..")

    // Call grammar validation API whenever the value changes
    useEffect(() => {
        generateHighlightedText();
        if (!value) {
            if(props.onValidationError)props.onValidationError([]);
            setErrors([]);
            return;
        }
        setIsLoading(true);
        const debounce = setTimeout(async () => {
            try {
                const resp = await validateGrammar(value);
                const err = resp?.matches.map((item: any) => ({
                    shortMessage: item?.shortMessage,
                    replacements: item?.replacements,
                    offset: item?.offset,
                    length: item?.length,
                }));
                setErrors(err);
                if (props?.onValidationError) props?.onValidationError(err);
                setIsLoading(false);
            } catch (error) { }
        }, 500);

        return () => clearTimeout(debounce);
    }, [value]);

    // Generate an array of ranges for the errors to visually highlight them
    const generateHighlightedText = () => {
        let highlightedText = value || props?.placeholder || "Write Here..";
        errors.forEach((error) => {
            const errorText = value.substring(error.offset, error.offset + error.length);
            // Wrap error words in a span with red underline
            const highlighted = `<span style='border-bottom: 2px solid red;'>${errorText}</span>`;
            highlightedText = highlightedText.replace(errorText, highlighted);
        });
        setHtml(highlightedText.endsWith('\n') ? highlightedText?.replaceAll(/\n/g, '<br>') + "<br>" : highlightedText?.replaceAll(/\n/g, '<br>'));
    };

    useEffect(() => {
        generateHighlightedText();
    }, [errors]);

    const handleScroll = () => {
        if (textareaRef.current && overlayRef.current) {
            overlayRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    const syncOverlayHeight = () => {
        if (textareaRef.current && overlayRef.current) {
            overlayRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
            handleScroll();
        }
    };

    useEffect(() => {
        syncOverlayHeight();
    }, [html])

    return (
        <div className="w-100 h-100 d-flex position-relative align-items-center overflow-hidden rounded">
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => {
                    syncOverlayHeight();
                    onChange(e.target.value);
                }}
                className={`${className}`}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        syncOverlayHeight();
                    }
                }}
                style={style}
                onFocus={onFocus}
                onBlur={onBlur}
                onScroll={handleScroll}
                placeholder={props.placeholder}
                onPaste={props.onPaste}
            />
            {isLoading && (
                <i className="fa fa-spinner fa-spin position-absolute end-0 px-3 z-1" />
            )}
            <div
                ref={overlayRef}
                className={className}
                style={{
                    ...style,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    pointerEvents: "none",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    color: "transparent",
                    backgroundColor: "transparent",
                    background: "transparent",
                    outline: "none",
                    border: "none"
                }}
                dangerouslySetInnerHTML={{ __html: html }} // Render the highlighted errors as HTML
            />
        </div>
    );
}
